import { createContext, useContext, useEffect, useState } from 'react'
import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { buyerApi, buyerSession } from '../lib/buyer'
import { useClock } from '../lib/hooks'
import { IconBox, IconCash, IconExit, IconStore, IconTruck } from '../ui/kit'
import { Shop } from './Shop'
import { MyOrders } from './MyOrders'
import { Arriving } from './Arriving'
import { MyBills } from './MyBills'

/**
 * The buyer's app — built for a market trader standing at a stall at five in the
 * morning, on a cheap phone, deciding what to buy today. Four screens, big type, and
 * never a number they have not been shown before they commit to it.
 */
export function BuyerShell() {
  const [signedIn, setSignedIn] = useState(buyerSession.isSignedIn)

  useEffect(() => {
    document.documentElement.setAttribute('data-density', 'comfortable')
    return () => document.documentElement.setAttribute('data-density', 'compact')
  }, [])

  if (!signedIn) return <Navigate to="/login/buyer" replace />

  return <SignedIn onSignOut={() => setSignedIn(false)} />
}

/** Signing out is reachable from every screen — a stall phone gets handed around. */
const SignOut = createContext<(() => void) | null>(null)

function SignedIn({ onSignOut }: { onSignOut: () => void }) {
  const nav = useNavigate()
  const now = useClock()
  const who = buyerSession.who

  function signOut() {
    if (!window.confirm('Sign out? You will need a new code to get back in.')) return
    buyerApi.signOut()
    onSignOut()
    nav('/login/buyer', { replace: true })
  }

  return (
    <SignOut.Provider value={signOut}>
      <div className="field-shell">
        <div className="field-status">
          <span>{now.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
          <span style={{ fontSize: 10, letterSpacing: '.06em' }}>{who?.name?.toUpperCase()}</span>
        </div>

        <Routes>
          <Route index element={<Shop />} />
          <Route path="orders" element={<MyOrders />} />
          <Route path="arriving" element={<Arriving />} />
          <Route path="bills" element={<MyBills />} />
          <Route path="*" element={<Navigate to="/b" replace />} />
        </Routes>

        <nav className="tabbar">
          <BuyerTab to="/b" label="Buy" icon={<IconStore size={19} />} end />
          <BuyerTab to="/b/orders" label="My orders" icon={<IconBox size={19} />} />
          <BuyerTab to="/b/arriving" label="Arriving" icon={<IconTruck size={19} />} />
          <BuyerTab to="/b/bills" label="Bills" icon={<IconCash size={19} />} />
        </nav>
      </div>
    </SignOut.Provider>
  )
}

function BuyerTab({ to, label, icon, end }: {
  to: string; label: string; icon: JSX.Element; end?: boolean
}) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}>
      {icon}
      <span>{label}</span>
    </NavLink>
  )
}

/** Shared header for the buyer screens. */
export function BuyerHeader({ title, back }: { title?: string; back?: () => void }) {
  const signOut = useContext(SignOut)
  return (
    <header className="field-head">
      {back ? (
        <button className="back" onClick={back}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.2" strokeLinecap="round"><path d="M15 5l-7 7 7 7" /></svg>
          {title ?? 'Back'}
        </button>
      ) : (
        <div className="field-brand">
          <span className="mark">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff"
              strokeWidth="2.2" strokeLinecap="round">
              <path d="M12 22V9M12 9c0-4 3-6 8-6 0 5-2.5 8-8 8z" />
              <path d="M12 13c0-3-2-5-6-5 0 4 1.8 6.4 6 6.6z" opacity=".75" />
            </svg>
          </span>
          Ledger
        </div>
      )}
      {signOut ? (
        <button className="header-signout" onClick={signOut}>
          <IconExit size={13} /> Sign out
        </button>
      ) : (
        <span className="pill" style={{ textTransform: 'none' }}>Buyer</span>
      )}
    </header>
  )
}
